import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, Chip, CircularProgress, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { getCalls } from '../../api/endpoints/calls';
import { formatCurrency } from '../../utils/formatters';

const statusColors = {
  completed: 'success',
  active: 'info',
  requested: 'warning',
  connecting: 'warning',
  missed: 'error',
  cancelled: 'default',
};

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

const CallLogs = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: rowsPerPage, search: debouncedSearch };
      if (statusFilter) params.status = statusFilter;
      const res = await getCalls(params);
      setCalls(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to fetch calls');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const columns = [
    { id: 'roomName', label: 'Room', sortable: true, render: (row) => (
      <strong style={{ fontSize: '0.85rem' }}>{row.roomName}</strong>
    )},
    { id: 'callType', label: 'Type', render: (row) => (
      <Chip label={row.callType === 'video' ? 'Video' : 'Audio'} size="small"
        color={row.callType === 'video' ? 'primary' : 'default'} variant="outlined" />
    )},
    { id: 'callerId', label: 'Caller', render: (row) => row.callerId?.name || 'N/A' },
    { id: 'astrologerId', label: 'Astrologer', render: (row) => row.astrologerId?.name || 'N/A' },
    { id: 'durationSeconds', label: 'Duration', render: (row) => formatDuration(row.durationSeconds) },
    { id: 'totalCost', label: 'Cost', render: (row) => row.totalCost > 0 ? formatCurrency(row.totalCost) : '-' },
    { id: 'status', label: 'Status', render: (row) => (
      <StatusChip status={row.status} label={row.status} color={statusColors[row.status] || 'default'} />
    )},
    { id: 'createdAt', label: 'Date', render: (row) => new Date(row.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) },
  ];

  return (
    <Box>
      <PageHeader title="Call Logs" subtitle="View all audio/video calls" />
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by room..." />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="requested">Requested</MenuItem>
            <MenuItem value="missed">Missed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <DataTable
          columns={columns}
          rows={calls}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={setPage}
          onRowsPerPageChange={(v) => { setRowsPerPage(v); setPage(1); }}
        />
      )}
    </Box>
  );
};

export default CallLogs;
